import { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDialogA11y } from '../dialog/useDialogA11y';
import UploadErrorDisplay from './UploadErrorDisplay';
import FileTypeDropdown from './FileTypeDropdown';
import { UploadError } from './uploadErrorHelper';
import { LegacyUploadFileMetadata, FileType, UploadProgress } from './types';
import { sortFilesByType, LEGACY_FILE_TYPE_LABELS } from './fileTypeUtils';
import './UploadModal.scss';

let uploadModalInstanceId = 0;

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  uploadedFiles: LegacyUploadFileMetadata[];
  draggedFiles: LegacyUploadFileMetadata[];
  uploadError: UploadError | null;
  isLoading: boolean;
  isUploadSuccessful: boolean;
  uploadProgress: UploadProgress | null;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDrop: (acceptedFiles: File[]) => void;
  handleUpload: () => void;
  clearFiles: () => void;
  removeFile: (fileName: string) => void;
  onFileTypeChange: (fileName: string, type: FileType) => void;
  onDismissError: () => void;
  isDragActive: boolean;
  getRootProps: any;
  getInputProps: any;
}

const UploadModal: FC<UploadModalProps> = ({
  isOpen,
  onClose,
  uploadedFiles,
  draggedFiles,
  uploadError,
  isLoading,
  isUploadSuccessful,
  uploadProgress,
  onChange,
  handleUpload,
  clearFiles,
  removeFile,
  onFileTypeChange,
  onDismissError,
  isDragActive,
  getRootProps,
  getInputProps,
}) => {
  const { t } = useTranslation();
  const dialogRef = useRef<HTMLDivElement>(null);
  const [titleId] = useState(() => {
    uploadModalInstanceId += 1;
    return `upload-modal-title-${uploadModalInstanceId}`;
  });

  useDialogA11y({ isOpen, onClose, containerRef: dialogRef });

  // Combine and sort files by type for display
  const allFiles = sortFilesByType([...draggedFiles, ...uploadedFiles]);
  const hasFiles = allFiles.length > 0;

  // Calculate progress percentage
  const progressPercentage = uploadProgress
    ? Math.round((uploadProgress.current / uploadProgress.total) * 100)
    : 0;

  if (!isOpen) return null;

  return (
    <div className="upload-modal-overlay">
      {/* Backdrop */}
      <div
        className="upload-modal-overlay__backdrop"
        onClick={onClose}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            onClose();
          }
        }}
        aria-label={t('Close modal')}
        role="button"
        tabIndex={0}
      />

      {/* Modal */}
      <div
        className="upload-modal"
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="upload-modal__content">
            {/* Modal Header */}
            <div className="upload-modal__header">
              <div className="upload-modal__header-content">
                <div className="upload-modal__header-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17,8 12,3 7,8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                </div>
                <div>
                  <h1 id={titleId} className="upload-modal__title">{t('Upload Data Files')}</h1>
                  <p className="upload-modal__subtitle">{t('Upload your TMS, Transport Order, and Add files')}</p>
                </div>
              </div>
              <button
                type="button"
                className="upload-modal__close"
                onClick={onClose}
                aria-label={t('Close')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="upload-modal__body">
              {/* Upload Progress Overlay */}
              {isLoading && uploadProgress && (
                <div className="upload-modal__progress-overlay">
                  <div className="upload-modal__progress-card">
                    <div className="upload-modal__progress-icon">
                      <svg className="upload-modal__progress-spinner" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 12a9 9 0 11-6.219-8.56" />
                      </svg>
                    </div>
                    <h3 className="upload-modal__progress-title">{t('Uploading files...')}</h3>
                    <p className="upload-modal__progress-file">{uploadProgress.currentFileName}</p>
                    <div className="upload-modal__progress-bar-container">
                      <div
                        className="upload-modal__progress-bar"
                        style={{ width: `${progressPercentage}%` }}
                      />
                    </div>
                    <p className="upload-modal__progress-text">
                      {t('File')} {uploadProgress.current} {t('of')} {uploadProgress.total} ({progressPercentage}%)
                    </p>
                  </div>
                </div>
              )}

              {/* Drag and Drop Area */}
              <div className={`upload-modal__dropzone ${isDragActive ? 'upload-modal__dropzone--active' : ''}`}>
                <div className="upload-modal__dropzone-inner" {...getRootProps()}>
                  <input
                    {...getInputProps()}
                    id="file-drop-upload"
                    className="upload-modal__dropzone-input"
                    disabled={isLoading}
                    data-testid="file-drop-upload"
                    aria-label={t('Upload files')}
                  />
                  <div className="upload-modal__dropzone-icon">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17,8 12,3 7,8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                  </div>
                  {isDragActive ? (
                    <p className="upload-modal__dropzone-text">{t('Drop files here')}</p>
                  ) : (
                    <>
                      <p className="upload-modal__dropzone-text">{t('Drag & drop files here')}</p>
                      <p className="upload-modal__dropzone-hint">{t('or click Browse to select files')}</p>
                    </>
                  )}
                </div>
              </div>

              {/* Browse Button */}
              <div className="upload-modal__browse-section">
                <label htmlFor="file-upload" className="upload-modal__browse-label">
                  <input
                    type="file"
                    id="file-upload"
                    className="upload-modal__browse-input"
                    onChange={onChange}
                    accept=".csv,.xlsx,.xls"
                    multiple
                    onClick={(event: React.MouseEvent<HTMLInputElement>) => {
                      const { currentTarget } = event;
                      currentTarget.value = '';
                    }}
                    disabled={isLoading}
                    data-testid="file-upload-input"
                  />
                  <span className="upload-modal__browse-btn">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                    </svg>
                    {t('Browse Files')}
                  </span>
                </label>
                <span className="upload-modal__file-types">{t('CSV, XLSX up to 10MB')}</span>
              </div>

              {/* Selected Files List */}
              {hasFiles && (
                <div className="upload-modal__file-list">
                  <div className="upload-modal__file-list-header">
                    <span className="upload-modal__file-count">
                      {allFiles.length} {allFiles.length === 1 ? t('file') : t('files')} {t('selected')}
                    </span>
                    <button
                      type="button"
                      className="upload-modal__clear-btn"
                      onClick={clearFiles}
                      disabled={isLoading}
                    >
                      {t('Clear all')}
                    </button>
                  </div>
                  <div className="upload-modal__file-list-info">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 16v-4" />
                      <path d="M12 8h.01" />
                    </svg>
                    <small>{t('Files upload in order: TMS → Transport Order → Add')}</small>
                  </div>
                  <ul className="upload-modal__files">
                    {allFiles.map((fileMetadata) => (
                      <li key={fileMetadata.file.name} className="upload-modal__file-item">
                        <div className="upload-modal__file-icon">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14,2 14,8 20,8" />
                          </svg>
                        </div>
                        <div className="upload-modal__file-info">
                          <span className="upload-modal__file-name">{fileMetadata.file.name}</span>
                          <span className="upload-modal__file-meta">
                            {(fileMetadata.file.size / 1024 / 1024).toFixed(2)} MB
                            {fileMetadata.detectedType !== FileType.UNKNOWN && (
                              <span className="upload-modal__auto-detected">
                                {t('Auto-detected')}: {t(LEGACY_FILE_TYPE_LABELS[fileMetadata.detectedType])}
                              </span>
                            )}
                          </span>
                        </div>
                        <FileTypeDropdown
                          value={fileMetadata.type}
                          onChange={(type) => onFileTypeChange(fileMetadata.file.name, type)}
                          disabled={isLoading}
                        />
                        <button
                          type="button"
                          className="upload-modal__file-remove"
                          onClick={() => removeFile(fileMetadata.file.name)}
                          disabled={isLoading}
                          aria-label={t('Remove file')}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Error Display */}
              {uploadError && (
                <UploadErrorDisplay
                  error={uploadError}
                  onDismiss={onDismissError}
                  onRetry={handleUpload}
                  onClearFile={clearFiles}
                  onTryDifferentFile={() => {
                    clearFiles();
                    document.getElementById('file-upload')?.click();
                  }}
                  onRefreshPage={() => window.location.reload()}
                />
              )}

              {/* Success Message */}
              {isUploadSuccessful && (
                <div className="upload-modal__success" data-testid="upload-success">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                    <polyline points="22,4 12,14.01 9,11.01" />
                  </svg>
                  <span>{t('Files uploaded successfully!')}</span>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="upload-modal__footer">
              <button
                type="button"
                className="upload-modal__cancel-btn"
                onClick={onClose}
                disabled={isLoading}
              >
                {t('Cancel')}
              </button>
              <button
                className="upload-modal__upload-btn"
                onClick={handleUpload}
                disabled={isLoading || !hasFiles}
                data-testid="upload-button"
                type="button"
              >
                {isLoading ? (
                  <>
                    <span className="upload-modal__btn-spinner" />
                    {t('Uploading...')}
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="17,8 12,3 7,8" />
                      <line x1="12" y1="3" x2="12" y2="15" />
                    </svg>
                    {t('Upload Files')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
  );
};

export default UploadModal;
